import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, Loader2, GraduationCap, Handshake, Building2, Users2, Trash2, ExternalLink, MapPin } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-card border border-border rounded-lg p-4">
    <div className="flex items-center gap-2 text-muted-foreground text-xs font-mono uppercase tracking-wider"><Icon className="h-4 w-4 text-primary" /> {label}</div>
    <div className="text-3xl font-serif mt-2 tabular-nums">{value}</div>
  </div>
);

const RankList = ({ title, rows }) => (
  <div className="bg-card border border-border rounded-lg p-4">
    <h3 className="font-serif text-lg mb-2">{title}</h3>
    {rows.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> : (
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.name} className="flex items-center justify-between text-sm">
            <span className="truncate pr-2">{r.name}</span>
            <span className="tabular-nums text-muted-foreground shrink-0">{r.count}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function CareerAdmin() {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/career/overview").then((r) => setData(r.data)).catch(() => setData({ stats: {}, top_industries: [], top_employers: [], jobs: [] }));
  useEffect(() => { load(); }, []);

  const deleteJob = async (j) => {
    if (!window.confirm(`Remove "${j.title}" at ${j.company}?`)) return;
    try { await api.delete(`/experience/jobs/${j.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not remove"); }
  };

  return (
    <div className="fade-up" data-testid="career-admin">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
        <h1 className="font-serif text-3xl tracking-tight mt-1 flex items-center gap-2"><Briefcase className="h-7 w-7 text-primary" /> Career Network</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Overview of your alumni professional network — mentors, networking opt-ins, top industries and employers, and the job board.</p>
      </div>

      {!data ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-16 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard icon={Handshake} label="Open to networking" value={data.stats.networking || 0} testid="career-stat-networking" />
            <StatCard icon={GraduationCap} label="Offering mentorship" value={data.stats.mentors || 0} testid="career-stat-mentors" />
            <StatCard icon={Users2} label="Seeking a mentor" value={data.stats.mentees || 0} testid="career-stat-mentees" />
            <StatCard icon={Briefcase} label="Active job posts" value={data.stats.active_jobs || 0} testid="career-stat-jobs" />
          </div>

          <div className="grid md:grid-cols-2 gap-3 mb-6">
            <RankList title="Top industries" rows={data.top_industries} />
            <RankList title="Top employers" rows={data.top_employers} />
          </div>

          <div className="bg-card border border-border rounded-lg p-4" data-testid="career-admin-jobs">
            <div className="flex items-center gap-2 mb-3"><Briefcase className="h-4 w-4 text-primary" /><h3 className="font-serif text-lg">Job postings</h3><span className="text-xs text-muted-foreground">({data.jobs.length})</span></div>
            {data.jobs.length === 0 ? <p className="text-sm text-muted-foreground">No job postings yet.</p> : (
              <div className="divide-y divide-border">
                {data.jobs.map((j) => (
                  <div key={j.id} data-testid={`admin-job-${j.id}`} className="flex items-start justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <div className="font-medium text-foreground">{j.title} <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-accent/20 text-primary">{j.type}</span></div>
                      <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{j.company}</span>
                        {(j.location || j.remote) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[j.location, j.remote ? "Remote" : null].filter(Boolean).join(" · ")}</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">Posted by {j.posted_by_name}{j.posted_by_role === "staff" ? " (Alumni Office)" : ""}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {(j.apply_url || j.apply_email) && <a href={j.apply_url || `mailto:${j.apply_email}`} target="_blank" rel="noreferrer" className="p-1.5 rounded-md border border-border hover:bg-muted" title="Open application"><ExternalLink className="h-4 w-4" /></a>}
                      <button data-testid={`admin-job-delete-${j.id}`} onClick={() => deleteJob(j)} className="p-1.5 rounded-md text-destructive hover:bg-destructive/10" title="Remove"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
